import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { indicate } from '@clean-code/shared/common';
import { BaseWidgetTypeContentComponent } from '@clean-code/shared/components/ui-dashboard';
import { ProgressBarComponent } from '@clean-code/shared/components/ui-progress-bar';
import { TooltipDirective } from '@clean-code/shared/components/ui-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, forkJoin, type Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { MarketHknCustomersWidgetTypeConfig } from '../market-hkn-customers';
import type {
  DashboardOrderHknVolumeAppliedFilters,
  DashboardOrderHknVolumeItem,
  HknTypeAmounts,
  MarketHknCustomersTableViewModel,
  PositionValuesByType,
} from '../models/market-hkn-customers.model';
import {
  buildMarketHknCustomersTableViewModel,
  getTypeTotal,
} from '../models/market-hkn-customers.model';
import { MarketHknCustomersService } from '../services/market-hkn-customers.service';

@Component({
  selector: 'dashboard-market-hkn-customers-content',
  templateUrl: './market-hkn-customers-content.component.html',
  host: {
    class: 'flex h-full flex-col overflow-hidden',
  },
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    ProgressBarComponent,
    TooltipDirective,
    FontAwesomeModule,
  ],
})
export class MarketHknCustomersContentComponent extends BaseWidgetTypeContentComponent<MarketHknCustomersWidgetTypeConfig> {
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public viewModel$: Observable<MarketHknCustomersTableViewModel>;
  public expandedCustomerIds = signal<Set<number>>(new Set());
  public expandedYearKeys = signal<Set<string>>(new Set());
  public faChevronDown = faChevronDown;
  public faChevronUp = faChevronUp;
  public isYearsMissing$ = new BehaviorSubject<boolean>(false);

  private marketHknCustomersService = inject(MarketHknCustomersService);

  public constructor() {
    super();

    // Track whether years is missing in the config
    this.configObservable.subscribe((value: MarketHknCustomersWidgetTypeConfig) => {
      const yearsMissing = !value?.filter?.years || value.filter.years.length === 0;
      this.isYearsMissing$.next(yearsMissing);
    });

    this.viewModel$ = this.configObservable.pipe(
      filter((value: MarketHknCustomersWidgetTypeConfig) => !!value.filter),
      filter((value: MarketHknCustomersWidgetTypeConfig) =>
        !!value.filter.years && value.filter.years.length > 0
      ),
      switchMap((value: MarketHknCustomersWidgetTypeConfig) => {
        const filterValue = value.filter;

        return forkJoin({
          customers: this.marketHknCustomersService.dashboardOrderHknVolume$(
            filterValue,
          ),
          appliedFilters:
            this.marketHknCustomersService.dashboardOrderHknVolumeAppliedFilters$(
              filterValue,
            ),
        }).pipe(indicate(this.isLoading$));
      }),
      map(({ customers, appliedFilters }) =>
        buildMarketHknCustomersTableViewModel(
          customers,
          appliedFilters,
        ),
      ),
    );
  }

  public toggleExpanded(customerId: number): void {
    const current = this.expandedCustomerIds();
    const next = new Set(current);
    if (next.has(customerId)) {
      next.delete(customerId);
    } else {
      next.add(customerId);
    }
    this.expandedCustomerIds.set(next);
  }

  public isExpanded(customerId: number): boolean {
    return this.expandedCustomerIds().has(customerId);
  }

  public toggleYearExpanded(customerId: number, deliveryYear: number): void {
    const yearKey = this.getYearKey(customerId, deliveryYear);
    const current = this.expandedYearKeys();
    const next = new Set(current);
    if (next.has(yearKey)) {
      next.delete(yearKey);
    } else {
      next.add(yearKey);
    }
    this.expandedYearKeys.set(next);
  }

  public isYearExpanded(customerId: number, deliveryYear: number): boolean {
    return this.expandedYearKeys().has(this.getYearKey(customerId, deliveryYear));
  }

  private getYearKey(customerId: number, deliveryYear: number): string {
    return `${customerId}-${deliveryYear}`;
  }

  public getTotalOpen(customer: DashboardOrderHknVolumeItem): number {
    return (customer.valuesByYear ?? []).reduce(
      (sum, year) =>
        sum +
        (year.positionValuesByType ?? []).reduce(
          (yearSum, pv) => yearSum + (pv.value?.openAmount ?? 0),
          0,
        ),
      0,
    );
  }

  public getTotalClosed(customer: DashboardOrderHknVolumeItem): number {
    return (customer.valuesByYear ?? []).reduce(
      (sum, year) =>
        sum +
        (year.positionValuesByType ?? []).reduce(
          (yearSum, pv) => yearSum + (pv.value?.closedAmount ?? 0),
          0,
        ),
      0,
    );
  }

  public getTotalContractAmount(customer: DashboardOrderHknVolumeItem): number {
    return (customer.valuesByYear ?? []).reduce(
      (sum, year) =>
        sum +
        (year.positionValuesByType ?? []).reduce(
          (yearSum, pv) => yearSum + (pv.value?.contractAmount ?? 0),
          0,
        ),
      0,
    );
  }

  public getCustomerTotalAmount(customer: DashboardOrderHknVolumeItem): number {
    return (customer.valuesByYear ?? []).reduce(
      (sum, year) => sum + (year.totalAmount ?? 0),
      0,
    );
  }

  public getGrandTotalClosed(customers: DashboardOrderHknVolumeItem[]): number {
    return (customers ?? []).reduce(
      (sum, customer) => sum + this.getTotalClosed(customer),
      0,
    );
  }

  public getGrandTotalContractAmount(customers: DashboardOrderHknVolumeItem[]): number {
    return (customers ?? []).reduce(
      (sum, customer) => sum + this.getTotalContractAmount(customer),
      0,
    );
  }

  public getGrandTotalOpen(customers: DashboardOrderHknVolumeItem[]): number {
    return (customers ?? []).reduce(
      (sum, customer) => sum + this.getTotalOpen(customer),
      0,
    );
  }

  public getGrandTotalAmount(customers: DashboardOrderHknVolumeItem[]): number {
    return (customers ?? []).reduce(
      (sum, customer) => sum + this.getCustomerTotalAmount(customer),
      0,
    );
  }

  public getYearTotalOpen(year: { positionValuesByType: PositionValuesByType[] }): number {
    return (year.positionValuesByType ?? []).reduce(
      (sum, pv) => sum + (pv.value?.openAmount ?? 0),
      0,
    );
  }

  public getYearTotalClosed(year: { positionValuesByType: PositionValuesByType[] }): number {
    return (year.positionValuesByType ?? []).reduce(
      (sum, pv) => sum + (pv.value?.closedAmount ?? 0),
      0,
    );
  }

  public getYearTotalContractAmount(year: { positionValuesByType: PositionValuesByType[] }): number {
    return (year.positionValuesByType ?? []).reduce(
      (sum, pv) => sum + (pv.value?.contractAmount ?? 0),
      0,
    );
  }

  public getTypeTotal(amounts: HknTypeAmounts): number {
    return getTypeTotal(amounts);
  }

  public hasTextValue(value: string | null | undefined): boolean {
    return typeof value === 'string' && !!value.trim();
  }

  public hasDateValue(value: string | Date | null | undefined): boolean {
    if (value == null) {
      return false;
    }

    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (typeof value === 'string') {
      return !!value.trim();
    }

    return false;
  }

  public hasListValue(values: Array<string | number> | null | undefined): boolean {
    return !!values?.length;
  }

  public hasTradeDateRange(appliedFilters: DashboardOrderHknVolumeAppliedFilters): boolean {
    return (
      this.hasDateValue(appliedFilters.tradeDateFrom) ||
      this.hasDateValue(appliedFilters.tradeDateTo)
    );
  }

  public hasAppliedFilters(appliedFilters: DashboardOrderHknVolumeAppliedFilters): boolean {
    return (
      this.hasTextValue(appliedFilters.marketAreaName) ||
      this.hasTextValue(appliedFilters.deliveryAreaName) ||
      this.hasTextValue(appliedFilters.segmentName) ||
      this.hasListValue(appliedFilters.kamNames) ||
      this.hasListValue(appliedFilters.companyNames) ||
      this.hasListValue(appliedFilters.years) ||
      this.hasTradeDateRange(appliedFilters)
    );
  }

  public joinValues(values: Array<string | number> | null | undefined): string {
    return (values ?? []).join(', ');
  }
}
